import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useCartAnimation } from '../../hooks/useCartAnimation';

export function AddToCartAnimation() {
  const { animations } = useCartAnimation();
  const [cartPosition, setCartPosition] = useState<{ x: number; y: number } | null>(null);
  const lastUpdateRef = useRef<number>(0);

  // Function to find and update cart position
  const updateCartPosition = () => {
    // Try multiple selectors for robustness
    const cartLink = 
      document.querySelector('[data-cart-target="true"]') ||
      document.querySelector('a[href="/cart"]');
    
    if (cartLink) {
      const rect = cartLink.getBoundingClientRect();
      setCartPosition({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      });
      lastUpdateRef.current = Date.now();
      return true;
    }
    return false;
  };

  // Initial setup and resize handler
  useEffect(() => {
    updateCartPosition();
    window.addEventListener('resize', updateCartPosition);
    
    // Also listen for scroll which can affect fixed elements
    window.addEventListener('scroll', updateCartPosition, { passive: true });
    
    return () => {
      window.removeEventListener('resize', updateCartPosition);
      window.removeEventListener('scroll', updateCartPosition);
    };
  }, []);

  // Re-check cart position when animations are triggered
  // This handles cases where BottomNav wasn't rendered yet on initial mount
  useEffect(() => {
    if (animations.length > 0) {
      // If we haven't found cart position yet, or it's been a while, refresh it
      const timeSinceLastUpdate = Date.now() - lastUpdateRef.current;
      if (!cartPosition || timeSinceLastUpdate > 1000) {
        // Small delay to ensure BottomNav is rendered
        const timer = setTimeout(updateCartPosition, 10);
        return () => clearTimeout(timer);
      }
    }
  }, [animations.length, cartPosition]);

  // Fallback: position at bottom-center if cart not found
  const targetPosition = cartPosition || {
    x: window.innerWidth / 2 - 60, // Approximate cart position (left side of center)
    y: window.innerHeight - 50,
  };

  return createPortal(
    <>
      {animations.map((animation) => {
        const deltaX = targetPosition.x - animation.startX;
        const deltaY = targetPosition.y - animation.startY;

        return (
          <div
            key={animation.id}
            className="pointer-events-none fixed z-[9999]"
            style={{
              left: animation.startX,
              top: animation.startY,
              animation: 'fly-to-cart 1200ms cubic-bezier(0.22, 0.61, 0.36, 1) forwards',
              '--delta-x': `${deltaX}px`,
              '--delta-y': `${deltaY}px`,
            } as React.CSSProperties}
          >
            <div className="relative -translate-x-1/2 -translate-y-1/2">
              {animation.productImage ? (
                <img
                  src={animation.productImage}
                  alt={animation.productName}
                  className="w-14 h-14 object-contain"
                  style={{
                    animation: 'product-scale 1200ms cubic-bezier(0.22, 0.61, 0.36, 1) forwards',
                  }}
                />
              ) : (
                <div 
                  className="w-14 h-14 flex items-center justify-center text-2xl"
                  style={{
                    animation: 'product-scale 1200ms cubic-bezier(0.22, 0.61, 0.36, 1) forwards',
                  }}
                >
                  📦
                </div>
              )}
            </div>
          </div>
        );
      })}
      <style>{`
        @keyframes fly-to-cart {
          0% {
            transform: translate(0, 0) scale(1);
            opacity: 1;
          }
          50% {
            opacity: 1;
          }
          100% {
            transform: translate(var(--delta-x), var(--delta-y)) scale(0.3);
            opacity: 0;
          }
        }
        
        @keyframes product-scale {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          100% {
            transform: scale(0.3);
            opacity: 0;
          }
        }
      `}</style>
    </>,
    document.body
  );
}
